import {BrowserModule} from "@angular/platform-browser";
import {FormsModule} from "@angular/forms";
import {HttpModule} from "@angular/http";
import {NgModule, ApplicationRef, LOCALE_ID} from "@angular/core";
import {removeNgStyles, createNewHosts, createInputTransfer} from "@angularclass/hmr";
import {RouterModule, PreloadAllModules} from "@angular/router";
/*
 * Platform and Environment providers/directives/pipes
 */
import {ENV_PROVIDERS} from "./environment";
import {ROUTES, appRoutingProviders} from "./app.routes";
// App is our top level component
import {AppComponent} from "./app.component";
import {APP_RESOLVER_PROVIDERS} from "./app.resolver";
import {AppState, InternalStateType} from "./app.service";
import "../styles/styles.scss";
import "../styles/headings.css";
import {IndexComponent} from "./components/index/index.component";
import {NavBarComponent} from "./components/nav-bar/nav-bar.component";
import {BusinessFormComponent} from "./components/business/form/business-form.component";
import {BusinessListComponent} from "./components/business/list/business-list.component";
import {LoginFormComponent} from "./components/login-form/login-form.component";
import {EventPageComponent} from "./components/event/event-page.component";
import {EventListComponent} from "./components/event/event-list/event-list.component";
import {EventDetailComponent} from "./components/event/event-detail/event-detail.component";
import {EventEditionComponent} from "./components/event/event-edition/event-edition.component";
import {EventCreationComponent} from "./components/event/event-creation/event-creation.component";
import {DashboardPageComponent} from "./components/dashboard/dashboard-page.component";
import {StatsComponent} from "./components/dashboard/stats/stats.component";
import {StatsDetailComponent} from "./components/dashboard/stats-detail/stats-detail.component";
import {AdminPageComponent} from "./components/admin/admin-page.component";
import {ProfilePageComponent} from "./components/profile/profile-page.component";
import {PricingPageComponent} from "./components/pricing/pricing-page.component";
import {AdminListComponent} from "./components/admin/admin-list/admin-list.component";
import {PhotoSwipeComponent} from "./components/photoswipe/photo-swipe.component";
import {PremiumPageComponent} from "./components/premium/premium-page.component";
import {TopBusinessComponent} from "./components/top-business/top-business.component";
import {PictureDetailComponent} from "./components/event/picture-detail/picture-detail.component";
import {QrCodeComponent} from "./components/qrcode/qr-code.component";
import {HelpPageComponent} from "./components/help/help-page.component";
import {ActivationComponent} from "./components/business/activation/activation.component";
import {PublicEventComponent} from "./components/public/event/public-event.component";
import {PhotoWallComponent} from "./components/event/photo-wall/photo-wall.component";
import {MyDatePickerModule} from "mydatepicker";
import {InfiniteScrollModule} from "angular2-infinite-scroll";
import {ModalModule} from "ng2-modal";
import {TooltipModule} from "ng2-tooltip";
import {SimpleNotificationsModule, NotificationsService} from "angular2-notifications";
import {FileUploadModule} from "ng2-file-upload";
import {CookieService} from "angular2-cookie/services/cookies.service";
import {CurrentBusinessService} from "./services/communication/currentBusinessService";
import {UrlService} from "./services/url.service";
import {ConfigurationService} from "./services/configuration.service";
import {RequestService} from "./services/request.service";
import {BusinessService} from "./services/data/business.service";
import {CurrentEventService} from "./services/communication/currentEventService";
import {UploadService} from "./services/uploadService";
import {CommentService} from "./services/data/comment.service";
import {PhotoSwipeService} from "./components/photoswipe/photo-swipe.service";
import {StatsService} from "./services/data/stats.service";
import {UserService} from "./services/data/user.service";
import {CurrentTransactionService} from "./services/communication/currentTransactionService";
import {PictureDetailService} from "./components/event/picture-detail/picture-detail.service";
import {PublicService} from "./services/data/public.service";
import {FileUploaderService} from "./services/communication/fileUploaderService";
import {AuthentService} from "./services/authent.service";
import {PublicPhotoWallComponent} from "./components/event/public-photo-wall/public-photo-wall.component";
import {PhotoWallService} from "./services/data/photo-wall.service";
import {ColorPickerModule} from "angular2-color-picker";
import {EventValidationComponent} from "./components/event/event-validation/event-validation.component";
import {NavBarService} from "./services/communication/nav-bar.service";
import {DiaporamaComponent} from "./components/event/diaporama/diaporama.component";
import {ClickOutsideModule} from "ng-click-outside";
import {StripeOrderComponent} from "./components/premium/stripe-order/stripe-order.component";
import {OrderEventComponent} from "./components/premium/order-event/order-event.component";
import {OrderAccountComponent} from "./components/premium/order-account/order-account.component";
import {StripeCallbackComponent} from "./components/premium/stripe-callback/stripe-callback.component";
import {StripeService} from "./services/data/stripe.service";
import {OrderMappingService} from "./services/data/order-mapping.service";
import {PaymentCanceledComponent} from "./components/payment/canceled/payment-canceled.component";
import {PaymentSuccessComponent} from "./components/payment/success/payment-success.component";
import {TweetWallComponent} from "./components/event/tweet-wall/tweet-wall.component";
import {TweetService} from "./services/data/tweet.service";

// Application wide providers
const APP_PROVIDERS = [
  ...APP_RESOLVER_PROVIDERS,
  AppState
];

type StoreType = {
  state: InternalStateType,
  restoreInputValues: () => void,
  disposeOldHosts: () => void
};


/**
 * `AppModule` is the main entry point into Angular2's bootstraping process
 */
@NgModule({
  bootstrap: [AppComponent],
  declarations: [
    AppComponent,
    IndexComponent,
    NavBarComponent,
    BusinessFormComponent,
    BusinessListComponent,
    LoginFormComponent,
    EventPageComponent,
    EventListComponent,
    EventDetailComponent,
    EventEditionComponent,
    EventCreationComponent,
    DashboardPageComponent,
    StatsComponent,
    StatsDetailComponent,
    AdminPageComponent,
    ProfilePageComponent,
    PricingPageComponent,
    AdminListComponent,
    PhotoSwipeComponent,
    PremiumPageComponent,
    TopBusinessComponent,
    PictureDetailComponent,
    QrCodeComponent,
    HelpPageComponent,
    ActivationComponent,
    PublicEventComponent,
    PhotoWallComponent,
    PublicPhotoWallComponent,
    EventValidationComponent,
    DiaporamaComponent,
    StripeOrderComponent,
    OrderEventComponent,
    OrderAccountComponent,
    StripeCallbackComponent,
    PaymentCanceledComponent,
    PaymentSuccessComponent,
    TweetWallComponent
  ],
  imports: [ // import Angular's modules
    BrowserModule,
    FormsModule,
    HttpModule,
    MyDatePickerModule,
    InfiniteScrollModule,
    ModalModule,
    TooltipModule,
    SimpleNotificationsModule,
    FileUploadModule,
    ColorPickerModule,
    ClickOutsideModule,
    RouterModule.forRoot(ROUTES, {useHash: true, preloadingStrategy: PreloadAllModules})
  ],
  providers: [ // expose our Services and Providers into Angular's dependency injection
    {provide: LOCALE_ID, useValue: "fr"},
    appRoutingProviders,
    ENV_PROVIDERS,
    APP_PROVIDERS,
    CookieService,
    CurrentBusinessService,
    UrlService,
    ConfigurationService,
    RequestService,
    ConfigurationService,
    BusinessService,
    CurrentEventService,
    UploadService,
    CommentService,
    PhotoSwipeService,
    StatsService,
    UserService,
    CurrentTransactionService,
    PictureDetailService,
    PublicService,
    FileUploaderService,
    AuthentService,
    NotificationsService,
    PhotoWallService,
    NavBarService,
    StripeService,
    OrderMappingService,
    TweetService
  ]
})
export class AppModule {

  constructor(public appRef: ApplicationRef,
              public appState: AppState) {
  }

  public hmrOnInit(store: StoreType) {
    if (!store || !store.state) {
      return;
    }
    console.log('HMR store', JSON.stringify(store, null, 2));
    // set state
    this.appState._state = store.state;
    // set input values
    if ('restoreInputValues' in store) {
      let restoreInputValues = store.restoreInputValues;
      setTimeout(restoreInputValues);
    }

    this.appRef.tick();
    delete store.state;
    delete store.restoreInputValues;
  }

  public hmrOnDestroy(store: StoreType) {
    const cmpLocation = this.appRef.components.map((cmp) => cmp.location.nativeElement);
    // save state
    const state = this.appState._state;
    store.state = state;
    // recreate root elements
    store.disposeOldHosts = createNewHosts(cmpLocation);
    // save input values
    store.restoreInputValues = createInputTransfer();
    // remove styles
    removeNgStyles();
  }

  public hmrAfterDestroy(store: StoreType) {
    // display new elements
    store.disposeOldHosts();
    delete store.disposeOldHosts;
  }

}



// WEBPACK FOOTER //
// node_modules/angular2-template-loader!./~/tslint-loader?{"configFile":"tslint.json"}!./src/app/app.module.ts